import { useCallback } from 'react';
import { SlotInfo, CalDateInfo } from '../../../types/schedule.types';
import { useCalDateUpdates } from '../../../hooks/dataUpdates';
import { normalizeToSortedIds } from '../../../utils/calDateSlotsSortUtils';

export interface UseCalDateSlotsSortParams {
  scheduleID: string | undefined;
  optimisticSlots: SlotInfo[];
  optimisticCalDates: CalDateInfo[];
  calDates: CalDateInfo[] | undefined;
  getDateSlots: (dateStr: string) => SlotInfo[];
  getDateCalDate: (dateStr: string) => CalDateInfo | undefined;
  refreshScheduleCalendarData: () => Promise<void>;
  /** 当成功更新某日 calDateSlotsSort 后触发（用于统一地点头归一化等后处理） */
  onAfterSortUpdated?: (dateStrs: string[]) => Promise<void>;
}

/**
 * Hook for updating calDateSlotsSort（slotID 扁平顺序）
 */
export const useCalDateSlotsSort = ({
  scheduleID,
  optimisticSlots,
  optimisticCalDates,
  calDates,
  getDateSlots,
  getDateCalDate,
  refreshScheduleCalendarData,
  onAfterSortUpdated,
}: UseCalDateSlotsSortParams) => {
  const { upsertCalDateData } = useCalDateUpdates();

  const updateCalDateSlotsSort = useCallback(
    async (
      dateStr: string,
      additionalSoltIds?: string[],
      excludeSoltIds?: string[],
      replaceIds?: string[],
      calDateShift?: string,
    ) => {
      if (!scheduleID) return;

      if (replaceIds !== undefined) {
        const sortIds = normalizeToSortedIds(replaceIds);
        await upsertCalDateData(scheduleID, {
          calDateDt: dateStr,
          calDateBelongScheduleID: scheduleID,
          calDateSlotsSort: sortIds,
          ...(calDateShift !== undefined ? { calDateShift } : {}),
        });
        await onAfterSortUpdated?.([dateStr]);
        return;
      }

      const excludeSet = excludeSoltIds ? new Set(excludeSoltIds) : new Set();
      const existingCalDate = (calDates ?? optimisticCalDates).find((pd) => {
        if (!pd.calDateDt) return false;
        return pd.calDateDt.split('T')[0] === dateStr;
      });

      if (additionalSoltIds?.length) {
        let base = normalizeToSortedIds(existingCalDate?.calDateSlotsSort);
        const append = additionalSoltIds.filter((id) => !base.includes(id));
        base = [...base, ...append];
        await upsertCalDateData(scheduleID, {
          calDateDt: dateStr,
          calDateBelongScheduleID: scheduleID,
          calDateSlotsSort: base,
        });
        await onAfterSortUpdated?.([dateStr]);
        return;
      }

      const dateSlots = optimisticSlots.filter((slot) => {
        if (!slot.slotDate || slot.slotDate.split('T')[0] !== dateStr) return false;
        return !excludeSet.has(slot.slotID);
      });

      if (dateSlots.length === 0) {
        if (existingCalDate?.calDateSlotsSort?.length) {
          await upsertCalDateData(scheduleID, {
            calDateDt: dateStr,
            calDateBelongScheduleID: scheduleID,
            calDateSlotsSort: [],
          });
          await onAfterSortUpdated?.([dateStr]);
        }
        return;
      }

      let next = normalizeToSortedIds(existingCalDate?.calDateSlotsSort);
      if (excludeSet.size > 0) {
        next = next.filter((id) => !excludeSet.has(id));
      } else {
        const existingIds = new Set(next);
        const toAppend = dateSlots
          .filter((t) => !existingIds.has(t.slotID))
          .map((t) => t.slotID);
        if (toAppend.length > 0) {
          next = [...next, ...toAppend];
        }
      }

      await upsertCalDateData(scheduleID, {
        calDateDt: dateStr,
        calDateBelongScheduleID: scheduleID,
        calDateSlotsSort: next,
      });
      await onAfterSortUpdated?.([dateStr]);
    },
    [
      scheduleID,
      optimisticSlots,
      calDates,
      optimisticCalDates,
      upsertCalDateData,
      onAfterSortUpdated,
    ],
  );

  /** 场景地点变更不再重写 sort，仅刷新数据 */
  const regroupAffectedCalDates = useCallback(async () => {
    await refreshScheduleCalendarData();
  }, [refreshScheduleCalendarData]);

  return { updateCalDateSlotsSort, regroupAffectedCalDates };
};
